// Utility functions for ItineraryPage

/**
 * Parse day activities from dayByDay format
 */
export const parseDayActivities = (day) => {
  const activities = [];
  
  // Check if activities array exists
  if (day.activities && Array.isArray(day.activities)) {
    day.activities.forEach(activity => {
      const activityText = typeof activity === 'string' ? activity : activity.activity || activity.name || '';
      if (activityText) {
        activities.push({ text: activityText, time: 'activity' });
      }
    });
  }
  
  // Check for time-based activities (morning, afternoon, evening)
  if (day.morning && Array.isArray(day.morning)) {
    day.morning.forEach(activity => {
      activities.push({ 
        text: typeof activity === 'string' ? activity : activity.activity || '', 
        time: 'morning' 
      });
    });
  }
  if (day.afternoon && Array.isArray(day.afternoon)) {
    day.afternoon.forEach(activity => {
      activities.push({ 
        text: typeof activity === 'string' ? activity : activity.activity || '', 
        time: 'afternoon' 
      });
    });
  }
  if (day.evening && Array.isArray(day.evening)) {
    day.evening.forEach(activity => {
      activities.push({ 
        text: typeof activity === 'string' ? activity : activity.activity || '', 
        time: 'evening' 
      });
    });
  }
  
  // If no activities found, try to extract from places
  if (activities.length === 0 && day.places && Array.isArray(day.places)) {
    day.places.forEach(place => {
      const placeName = typeof place === 'string' ? place : place.name || place;
      activities.push({ text: `Visit ${placeName}`, time: 'activity' });
    });
  }
  
  return activities;
};

/**
 * Extract time from activity string (e.g., "10:00 AM | Activity name")
 */
export const extractTimeFromActivity = (activityString) => {
  if (!activityString) return { time: null, title: activityString };
  const match = activityString.match(/^(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm))\s*\|\s*(.+)$/);
  if (match) {
    return { time: match[1], title: match[2].trim() };
  }
  return { time: null, title: activityString };
};

/**
 * Get next activity for "Next Up" section
 */
export const getNextActivity = (currentActivities, currentIndex, timeBlock) => {
  // Check if there's a next activity in the same time block
  if (currentIndex < currentActivities.length - 1) {
    const nextActivity = currentActivities[currentIndex + 1];
    if (nextActivity && typeof nextActivity === 'object' && nextActivity.activity) {
      const { title } = extractTimeFromActivity(nextActivity.activity);
      return title;
    }
  }
  // Check transit time to next time block
  if (currentActivities[currentIndex] && typeof currentActivities[currentIndex] === 'object') {
    const transitTime = currentActivities[currentIndex].transitTimeFromPrevious;
    if (transitTime) {
      return transitTime;
    }
  }
  return null;
};

/**
 * Generate unique tagline for each itinerary
 */
export const generateItineraryTagline = (itinerary, location = null) => {
  const days = itinerary.durationDays || itinerary.days || 0;
  const type = itinerary.itineraryType;
  
  // Extract place name with priority:
  // For existing itineraries, prioritize itinerary data over URL query params
  // 1. Extract from itinerary.title (e.g., "3-Day Tour of Ayodhaya" -> "Ayodhaya")
  // 2. First place from placesCovered array
  // 3. Extract from description/tripSummary
  // 4. itinerary.place (if exists)
  // 5. location.state.formData.place (only for newly created itineraries)
  // 6. itinerary.country (fallback)
  // Note: Don't use formData.place from URL query params as it may be for a different search
  
  let placeName = null;
  
  // First, try to extract from itinerary title
  if (itinerary.title) {
    // Pattern: "... in [Place]" or "... of [Place]" or "... for [Place]"
    const titleMatch = itinerary.title.match(/(?:in|of|for)\s+([^,]+?)(?:\s*[,-]|$)/i);
    if (titleMatch && titleMatch[1]) {
      placeName = titleMatch[1].trim();
    }
  }
  
  // If not found, try placesCovered array
  if (!placeName && itinerary.placesCovered && Array.isArray(itinerary.placesCovered) && itinerary.placesCovered.length > 0) {
    const firstPlace = itinerary.placesCovered[0];
    placeName = typeof firstPlace === 'string' ? firstPlace : firstPlace.name || firstPlace;
  }
  
  // If still not found, try to extract from description/tripSummary
  if (!placeName) {
    const description = itinerary.description || itinerary.tripSummary || '';
    // Look for patterns like "in [Place]", "of [Place]", "to [Place]"
    const descMatch = description.match(/(?:in|of|to)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/);
    if (descMatch && descMatch[1]) {
      placeName = descMatch[1].trim();
    }
  }
  
  // Use itinerary.place if available
  if (!placeName) {
    placeName = itinerary.place;
  }
  
  // Only use location.state.formData.place for newly created itineraries (not URL query params)
  if (!placeName && location?.state?.formData?.place) {
    placeName = location.state.formData.place;
  }
  
  // Final fallback to country
  placeName = placeName || itinerary.country || 'Your Destination';
  
  // Type-specific taglines
  const typeTaglines = {
    cultural: 'Cultural Journey',
    culinary: 'Culinary Adventure',
    gastronomy: 'Gastronomic Experience',
    romantic: 'Romantic Escape',
    adventure: 'Adventure Quest',
    party: 'Party & Nightlife',
    peace: 'Peaceful Retreat',
    relax: 'Relaxation Getaway',
    family: 'Family Fun',
    solo: 'Solo Explorer',
    nature: 'Nature Expedition',
    historical: 'Historical Discovery',
    beach: 'Beach Paradise',
    mountain: 'Mountain Adventure'
  };
  
  const typeLabel = type && typeTaglines[type.toLowerCase()] 
    ? typeTaglines[type.toLowerCase()] 
    : 'Travel Experience';
  
  // Generate tagline based on duration and type
  if (days > 0) {
    return `${days}-Day ${typeLabel} in ${placeName}`;
  } else {
    return `${typeLabel} in ${placeName}`;
  }
};

/**
 * Categorize day activities by type (Museum, Food, Nature, etc.)
 * @param {Array} dayActivities - Array of activity objects from a single day
 * @returns {Array<{ category: string, count: number, activityIndices: number[] }>}
 */
export const categorizeActivities = (dayActivities) => {
  if (!dayActivities || !Array.isArray(dayActivities)) return [];

  const CATEGORY_KEYWORDS = {
    Museum: ['museum', 'gallery', 'exhibit', 'art'],
    Food: ['food', 'restaurant', 'cafe', 'dining', 'cuisine', 'eat', 'brunch', 'lunch', 'dinner', 'culinary', 'market', 'bakery', 'bistro'],
    Nature: ['park', 'garden', 'hike', 'trail', 'nature', 'beach', 'lake', 'mountain', 'forest', 'waterfall'],
    Culture: ['temple', 'church', 'mosque', 'shrine', 'cathedral', 'heritage', 'cultural', 'ceremony', 'festival'],
    Shopping: ['shop', 'shopping', 'bazaar', 'souk', 'mall', 'boutique', 'store'],
    Nightlife: ['bar', 'club', 'nightlife', 'pub', 'lounge', 'cocktail'],
    Sightseeing: ['tour', 'view', 'monument', 'landmark', 'palace', 'castle', 'tower', 'bridge', 'square'],
    Adventure: ['adventure', 'sport', 'surf', 'dive', 'climb', 'kayak', 'cycle', 'zip'],
    Relaxation: ['spa', 'relax', 'wellness', 'massage', 'bath', 'retreat', 'yoga'],
  };

  const categories = {};

  dayActivities.forEach((activity, index) => {
    const text = (
      typeof activity === 'string'
        ? activity
        : (activity.activity || activity.name || activity.place || '')
    ).toLowerCase();

    let matched = false;
    for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
      if (keywords.some(kw => text.includes(kw))) {
        if (!categories[category]) categories[category] = { category, count: 0, activityIndices: [] };
        categories[category].count++;
        categories[category].activityIndices.push(index);
        matched = true;
        break;
      }
    }
    if (!matched) {
      if (!categories['Other']) categories['Other'] = { category: 'Other', count: 0, activityIndices: [] };
      categories['Other'].count++;
      categories['Other'].activityIndices.push(index);
    }
  });

  return Object.values(categories).sort((a, b) => b.count - a.count);
};

/**
 * Clean notes about missing data from text
 */
export const cleanMissingDataNote = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(/Note:.*?unavailable.*?SafarGlimpse.*?database.*?\.?/gi, '')
    .replace(/Note:.*?unavailable.*?\.?/gi, '')
    .replace(/Note:.*?MISSING_ITEM.*?\.?/gi, '')
    .replace(/Note:.*?best available data.*?unavailable.*?\.?/gi, '')
    .trim();
};

/**
 * Check if array/string has meaningful content
 */
export const hasContent = (value) => {
  if (!value) return false;
  if (Array.isArray(value)) {
    return value.length > 0 && value.some(item => {
      if (typeof item === 'string') {
        const cleaned = cleanMissingDataNote(item);
        return cleaned && cleaned.trim().length > 0;
      }
      return item != null;
    });
  }
  if (typeof value === 'string') {
    const cleaned = cleanMissingDataNote(value);
    return cleaned && cleaned.trim().length > 0;
  }
  return true;
};

